import React, { useState } from 'react';
import { TaskItem } from '../../../types/tasks';
import { useTasks } from '../../../context/TasksContext';
import { addDays, daysBetween, shortDate } from '../../../utils/taskDates';
import { floats, hasConflict, isCritical, isLate, planClasses, rangeLabel } from '../../../utils/projectPlan';
import ProgressCheck from '../../tasks/ProgressCheck';
import ItemActions from '../ItemActions';

export interface GanttRowData {
  item: TaskItem;
  depth: number;
  hasChildren: boolean;
  /** Main branch it's under — picks its colour, as on the map. */
  branch: number;
}

export type BarDrag = 'move' | 'resize' | 'link';

interface GanttRowProps {
  row: GanttRowData;
  /** Planned dates as drawn (a drag in progress or just dropped shows here first). */
  dates: { start: string; finish: string } | null;
  from: string;
  px: number;
  readOnly: boolean;
  selected: boolean;
  flash: boolean;
  collapsed: boolean;
  linkTarget: boolean;
  onSelect: () => void;
  onToggle: () => void;
  onAddChild: () => void;
  onBarPointerDown: (e: React.PointerEvent, mode: BarDrag) => void;
  onScheduleAt: (day: string) => void;
  /** Press on the empty part of the row to drag out a date range. */
  onTrackPointerDown: (e: React.PointerEvent) => void;
  /** The range being dragged out on this row. */
  range: { start: string; finish: string } | null;
}

/**
 * One line of the Gantt chart: the topic (tick, name, people, ⋯ — as on a
 * list) pinned on the left, its bar on the timeline. A branch draws a bracket
 * over what's under it; a milestone a diamond; an event a band.
 */
const GanttRow: React.FC<GanttRowProps> = (props) => {
  const { row, dates, from, px, readOnly } = props;
  const { item } = row;
  const { updateItem, today } = useTasks();
  const [renaming, setRenaming] = useState<string | null>(null);
  const done = item.progress >= 100;
  const kind = item.node_kind || 'task';
  const summary = row.hasChildren;

  const x = (day: string) => daysBetween(from, day) * px;
  const bar = dates
    ? { left: x(dates.start), width: Math.max(px * (daysBetween(dates.start, dates.finish) + 1), 4) }
    : null;
  const late = isLate(item);
  const states = planClasses(item);
  const tip = dates
    ? `${item.text || 'Topic'} · ${rangeLabel(dates.start, dates.finish)}${
        item.slack_days != null && !done && kind !== 'milestone' && kind !== 'event'
          ? late ? ` · ${-item.slack_days}d late` : item.slack_days === 0 ? ' · no time to spare' : ` · ${item.slack_days}d to spare`
          : ''
      }${isCritical(item) ? ' · critical path' : ''}${hasConflict(item) ? ` · conflict: starts ${item.conflict_days}d before what it waits for is done` : ''}${
        item.due_date && !(floats(item) && item.start_date) ? ` · due ${shortDate(item.due_date)}` : ''
      }${
        floats(item) ? ' · starts when what it waits for is done' : ''
      }`
    : '';

  const commitName = () => {
    const next = (renaming ?? '').trim();
    setRenaming(null);
    if (next && next !== item.text) updateItem(item.id, { text: next });
  };

  return (
    <div
      className={`tp-g-row${props.selected ? ' is-selected' : ''}${props.flash ? ' is-flash' : ''}${done ? ' is-done' : ''}`}
      style={{ '--tp-branch': `var(--tp-branch-${row.branch % 8})` } as React.CSSProperties}
      data-gantt-id={item.id}
      onClick={props.onSelect}
    >
      <div className="tp-g-cell" style={{ paddingLeft: 6 + row.depth * 16 }}>
        {row.hasChildren ? (
          <button
            type="button"
            className={`tp-g-fold${props.collapsed ? ' is-collapsed' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              props.onToggle();
            }}
            aria-label={props.collapsed ? 'Expand' : 'Collapse'}
          >
            ▸
          </button>
        ) : (
          <span className="tp-g-fold-spacer" />
        )}
        <ProgressCheck
          value={item.progress}
          onToggle={() => !readOnly && updateItem(item.id, { progress: done ? 0 : 100 })}
          disabled={readOnly}
          label={`${done ? 'Uncheck' : 'Check'} ${item.text || 'topic'}`}
          size={18}
        />
        {renaming !== null ? (
          <input
            className="tp-g-rename"
            value={renaming}
            autoFocus
            onChange={(e) => setRenaming(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitName();
              if (e.key === 'Escape') setRenaming(null);
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label="Topic name"
          />
        ) : (
          <span
            className={`tp-g-name${row.depth === 0 ? ' is-main' : ''}`}
            title={item.text}
            onDoubleClick={() => !readOnly && setRenaming(item.text)}
          >
            {item.text || 'Topic'}
          </span>
        )}
        <ItemActions item={item} readOnly={readOnly} hasChildren={row.hasChildren} onAddChild={props.onAddChild} maxPeople={1} />
      </div>

      <div
        className={`tp-g-track${!readOnly && !summary ? ' is-pickable' : ''}`}
        onPointerDown={(e) => {
          if (!readOnly && !summary) props.onTrackPointerDown(e);
        }}
        onDoubleClick={(e) => {
          if (readOnly || dates || summary) return;
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          props.onScheduleAt(addDays(from, Math.floor((e.clientX - rect.left) / px)));
        }}
        title={!summary && !readOnly ? 'Drag across days to set its dates' : undefined}
      >
        {props.range && (
          <div
            className="tp-g-range"
            style={{ left: x(props.range.start), width: (daysBetween(props.range.start, props.range.finish) + 1) * px }}
          >
            <span>
              {rangeLabel(props.range.start, props.range.finish)}
              {kind !== 'milestone' && ` · ${daysBetween(props.range.start, props.range.finish) + 1}d`}
            </span>
          </div>
        )}
        {bar && summary && (
          <div className={`tp-g-summary ${states}`} style={{ left: bar.left, width: bar.width }} title={tip} />
        )}
        {bar && !summary && kind === 'milestone' && (
          <div
            className={`tp-g-diamond${done ? ' is-done' : ''} ${states}${props.linkTarget ? ' is-link-target' : ''}`}
            style={{ left: bar.left + px / 2 }}
            title={tip}
            onPointerDown={(e) => props.onBarPointerDown(e, 'move')}
          >
            {!readOnly && <span className="tp-g-handle tp-g-link" onPointerDown={(e) => { e.stopPropagation(); props.onBarPointerDown(e, 'link'); }} title="Drag to a task that waits for this" />}
          </div>
        )}
        {bar && !summary && kind !== 'milestone' && (
          <div
            className={[
              'tp-g-bar',
              kind === 'event' ? 'is-event' : '',
              done ? 'is-done' : '',
              states,
              props.linkTarget ? 'is-link-target' : '',
              readOnly || floats(item) ? '' : 'is-movable',
            ].join(' ')}
            style={{ left: bar.left, width: bar.width }}
            title={tip}
            onPointerDown={(e) => props.onBarPointerDown(e, 'move')}
          >
            <span className="tp-g-progress" style={{ width: `${item.progress}%` }} />
            {px >= 14 && bar.width > 60 && <span className="tp-g-label">{item.text}</span>}
            {!readOnly && (
              <>
                <span className="tp-g-handle tp-g-resize" onPointerDown={(e) => { e.stopPropagation(); props.onBarPointerDown(e, 'resize'); }} title="Drag to change how long it takes" />
                <span className="tp-g-handle tp-g-link" onPointerDown={(e) => { e.stopPropagation(); props.onBarPointerDown(e, 'link'); }} title="Drag to a task that waits for this" />
              </>
            )}
          </div>
        )}
        {item.due_date && !done && kind === 'task' && (
          <span
            className={`tp-g-deadline${item.due_date < today ? ' is-past' : ''}`}
            style={{ left: x(item.due_date) + px - 1 }}
            title={`Due ${shortDate(item.due_date)}`}
          />
        )}
      </div>
    </div>
  );
};

export default GanttRow;
